import { observer } from "mobx-react"
import { useRouter } from "next/router"
import { useMemo } from "react"

import Button from "~/components/atoms/Button"
import Link from "~/components/atoms/Link"
import Typography from "~/components/atoms/Typography"
import ProgressCircle from "~/components/molecules/ProgressCircle"
import Header from "~/components/organisms/Header"
import { useMobileMedia } from "~/hooks/useMobileMedia"
import { internalLink } from "~/services/helper"
import { useStores } from "~/stores/StoreProvider"

import * as Styled from "./styled"

type ActionType = "non_login" | "complete" | "progressing"

const CONST_DATA = {
  non_login: {
    url: "/auth/register?access_from=new-grad-scout",
    top_info_text: "",
    top_button_copy: "",
    top_button_label: "今すぐ始める",
    bottom_button_copy: "＼3分で簡単に登録できます／",
    bottom_button_label: "会員登録して始める",
  },
  progressing: {
    url: "/input-profile-flow?access_from=new-grad-scout",
    top_info_text: `プロフィールを充実させて\nあなたにマッチした(\n)オファーを受け取ろう！`,
    top_button_copy: "＼ここさえ押さえれば完璧！最重要項目をカンタン入力／",
    top_button_label: "プロフィールを入力する",
    bottom_button_copy: `プロフィールを充実させて\nあなたにマッチしたオファーを受け取ろう！`,
    bottom_button_label: "プロフィールを入力する",
  },
  complete: {
    url: "/profile?access_from=new-grad-scout",
    top_info_text: "スカウトが届いたら(\n)気軽に返信してみましょう",
    top_button_copy: "＼更にプロフィールを充実・編集したい方はこちら／",
    top_button_label: "プロフィール画面を開く",
    bottom_button_copy: `プロフィールを充実させて\nあなたにマッチしたオファーを受け取ろう！`,
    bottom_button_label: "プロフィール画面を開く",
  },
}

const addNewLineOnSp = (isMobile: boolean, text: string) => {
  return isMobile ? text.replace("(\n)", "\n") : text.replace("(\n)", "")
}

export const NewGradScout: React.VFC = observer(() => {
  const { inputProfileFlowStore } = useStores()
  const isMobile = useMobileMedia()

  const { actionType } = useMemo(() => {
    const enteredRate = inputProfileFlowStore.enteredRate
    let actionType: ActionType
    if (enteredRate === undefined) {
      actionType = "non_login"
    } else if (enteredRate === 100) {
      actionType = "complete"
    } else {
      actionType = "progressing"
    }
    return { actionType }
  }, [inputProfileFlowStore.enteredRate])

  return (
    <Styled.AllContainer>
      <Header showTab logoType="NewGradScout" />
      <div className="main-visual">
        <div className="main-visual-inner">
          <div className="heading-container">
            <div className="heading-1-sub-1">
              <p className="heading-1-sub-1-text">定型文のスカウトメールばかりでうんざり？</p>
            </div>
            <h1 className="heading-1">
              <Typography type="Hero" className="heading-1-text">
                あなたの <span className="strong">ONLY ONE</span> な経験に{isMobile ? "" : "、"}
                <br />
                <span className="strong">ONE to ONE</span> のスカウトを
              </Typography>
            </h1>
            <div className="heading-1-sub-2">
              <Typography className="heading-1-sub-2-text" type="SubHeading">
                あなたの経験を評価する企業から
                <br />
                1人ひとりに個別でスカウトが届きます
              </Typography>
            </div>
            <MainActionInfo enteredRate={inputProfileFlowStore.enteredRate} type={actionType} />
          </div>
        </div>
      </div>

      <div className="main-contents">
        <div className="features-container">
          <h2 className="heading-2">
            <Typography type="Hero" className="heading-2-text">
              ご利用の流れ
            </Typography>
          </h2>
          <div className="features-contents">
            <div className="each-feature">
              <div className="each-feature-heading">
                <div className="each-feature-num">
                  <span>STEP</span>1
                </div>
                <Typography className="each-feature-title" type="Heading">
                  会員登録して
                  <br />
                  プロフィールを作成
                </Typography>
              </div>
              <div className="each-feature-img feature-1" />
              <p className="each-feature-description">
                まずはあなただけの Only One なプロフィールを作成しましょう。無料の自己 PR 作成会も開催中です。
              </p>
            </div>
            <div className="each-feature-arrow" />
            <div className="each-feature">
              <div className="each-feature-heading">
                <div className="each-feature-num">
                  <span>STEP</span>2
                </div>
                <Typography className="each-feature-title" type="Heading">
                  採用担当者から
                  <br />
                  コメント形式で
                  <br />
                  個別スカウトが届く
                </Typography>
              </div>
              <div className="each-feature-img feature-2" />
              <p className="each-feature-description">
                プロフィールのどこに興味を持ったかを添えた形で、あなたの経験を評価した企業から One to One
                のスカウトが届きます。
              </p>
            </div>
            <div className="each-feature-arrow" />
            <div className="each-feature">
              <div className="each-feature-heading">
                <div className="each-feature-num">
                  <span>STEP</span>3
                </div>
                <Typography className="each-feature-title" type="Heading">
                  チャットライクな
                  <br />
                  コミュニケーション
                </Typography>
              </div>
              <div className="each-feature-img feature-3" />
              <p className="each-feature-description">
                畏まった返信は不要です。チャット感覚で気軽に企業とコミュニケーションを取り、気になった企業があれば説明会や選考に参加しましょう。
              </p>
            </div>
          </div>

          <div className="main-action-button-wrapper">
            <p className="main-action-button-label">
              {addNewLineOnSp(isMobile, CONST_DATA[actionType].bottom_button_copy)}
            </p>
            <MainActionButton
              textForGuest={CONST_DATA[actionType].bottom_button_label}
              size="large"
              type={actionType}
            />
            {actionType === "non_login" && (
              <p className="main-action-button-text">
                Infra インターンのアカウントをお持ちの方は
                <Link href={`${process.env.INFRA_INTERN_URL}/auth/login?access_from=new-grad-scout`}>こちら</Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </Styled.AllContainer>
  )
})

type MainActionInfoProps = {
  enteredRate?: number
  type: ActionType
}
export const MainActionInfo = ({ enteredRate, type }: MainActionInfoProps) => {
  const isMobile = useMobileMedia()

  if (enteredRate === undefined) {
    return (
      <>
        <div className="heading-1-button">
          <MainActionButton textForGuest={CONST_DATA[type].top_button_label} size="medium" type="non_login" />
          <p className="heading-1-button-text">
            Infra インターンのアカウントをお持ちの方は
            <Link href={`${process.env.INFRA_INTERN_URL}/auth/login?access_from=new-grad-scout`}>こちら</Link>
          </p>
        </div>
      </>
    )
  }

  const GuideInfo = () => (
    <div>
      {type === "complete" && (
        <Typography className="heading-1-guide-info-heading-complete" type="SubHeading">
          基本プロフィール入力完了
        </Typography>
      )}
      <Typography className="heading-1-guide-info-heading" type="SubHeading">
        {addNewLineOnSp(isMobile, CONST_DATA[type].top_info_text)}
      </Typography>
    </div>
  )

  const GuideCircle = () => (
    <div className="heading-1-guide-circle">
      <ProgressCircle percentage={enteredRate} labelSize={isMobile ? 28 : 37} unitSize={12} />
    </div>
  )

  const GuideAction = () => (
    <div className="heading-1-guide-action">
      <Typography className="heading-1-guide-info-sub-label" type="SubLabel">
        {CONST_DATA[type].top_button_copy}
      </Typography>
      <MainActionButton textForGuest={CONST_DATA[type].top_button_label} size="small" type={type} />
    </div>
  )

  return (
    <div className="heading-1-guide">
      {!isMobile && (
        <>
          <div className="heading-1-guide-layout-pc">
            <GuideInfo />
            <GuideAction />
          </div>
          <GuideCircle />
        </>
      )}
      {isMobile && (
        <>
          <div className="heading-1-guide-layout-sp">
            <GuideCircle />
            <GuideInfo />
          </div>
          <GuideAction />
        </>
      )}
    </div>
  )
}

type MainActionButtonProps = {
  textForGuest: string
  type: ActionType
  size: "small" | "medium" | "large"
}
const MainActionButton = ({ textForGuest, size, type }: MainActionButtonProps) => {
  const router = useRouter()

  const onClick = () => {
    const url =
      type === "progressing"
        ? internalLink(CONST_DATA[type].url) + `&close_url=${window.location.href}`
        : internalLink(CONST_DATA[type].url)
    router.push(url)
  }

  return (
    <Button className={`main-action-button ${size}`} size="Medium" onClick={onClick}>
      {textForGuest}
    </Button>
  )
}
